import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  fetchContactCampaignEnrollments,
  enrollContactInCampaign,
  unenrollContactFromCampaign,
  subscribeToQueueByContact,
  transformQueueItemToCamelCase,
  transformNodeToCamelCase,
  transformUserCampaignToCamelCase,
} from '../api/supabase/automations';
import { useAuth } from '../contexts/AuthContext';

/**
 * Walks the linked-list of nodes starting from the trigger's first child and
 * returns them in execution order. Falls back to orderIndex sort when the
 * linked-list is broken or incomplete.
 */
function buildOrderedNodeChain(nodes) {
  if (!nodes.length) return [];

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const referencedAsNext = new Set();
  nodes.forEach((n) => {
    if (n.nextNodeId) referencedAsNext.add(n.nextNodeId);
    if (n.falseNodeId) referencedAsNext.add(n.falseNodeId);
  });

  // The head is the node that is nobody's nextNodeId/falseNodeId
  let head = nodes.find((n) => !referencedAsNext.has(n.id));
  if (!head) head = nodes[0];

  const ordered = [];
  const visited = new Set();
  let current = head;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    ordered.push(current);
    current = current.nextNodeId ? byId.get(current.nextNodeId) : null;
  }

  // Append any nodes missed by the chain walk (false branches, orphans)
  nodes.forEach((n) => {
    if (!visited.has(n.id)) ordered.push(n);
  });

  return ordered;
}

function deriveEnrollmentStatus(queueItems) {
  if (!queueItems.length) return 'completed';
  const statuses = queueItems.map((q) => q.status);
  if (statuses.includes('failed')) return 'failed';
  if (statuses.includes('processing')) return 'active';
  if (statuses.includes('pending')) return 'active';
  if (statuses.includes('paused')) return 'paused';
  if (statuses.every((s) => s === 'completed' || s === 'cancelled')) return 'completed';
  return 'active';
}

function buildEnrollments(rawQueueItems, rawNodes) {
  const queueByCampaign = new Map();
  const campaignMeta = new Map();

  rawQueueItems.forEach((raw) => {
    const q = transformQueueItemToCamelCase(raw);
    const ucId = q.userCampaignId;
    if (!queueByCampaign.has(ucId)) queueByCampaign.set(ucId, []);
    queueByCampaign.get(ucId).push(q);

    if (!campaignMeta.has(ucId) && raw.user_campaign) {
      campaignMeta.set(ucId, transformUserCampaignToCamelCase(raw.user_campaign));
    }
  });

  const nodesByCampaign = new Map();
  rawNodes.forEach((raw) => {
    const n = transformNodeToCamelCase(raw);
    const ucId = n.userCampaignId;
    if (!nodesByCampaign.has(ucId)) nodesByCampaign.set(ucId, []);
    nodesByCampaign.get(ucId).push(n);
  });

  const enrollments = [];

  for (const [ucId, queueItems] of queueByCampaign) {
    const userCampaign = campaignMeta.get(ucId) || { id: ucId };
    const template = userCampaign.template || {};
    const campaignNodes = nodesByCampaign.get(ucId) || [];
    const orderedNodes = buildOrderedNodeChain(campaignNodes);

    // Only count action nodes (exclude TRIGGER type) for progress
    const actionNodes = orderedNodes.filter((n) => n.nodeType !== 'TRIGGER');

    const completedNodeIds = new Set();
    const failedNodeIds = new Set();
    let currentStep = null;

    queueItems.forEach((q) => {
      if (q.status === 'completed') completedNodeIds.add(q.currentNodeId);
      if (q.status === 'failed') failedNodeIds.add(q.currentNodeId);
      if ((q.status === 'pending' || q.status === 'processing') && !currentStep) {
        const node = orderedNodes.find((n) => n.id === q.currentNodeId);
        currentStep = { node: node || null, queueItem: q };
      }
    });

    // Map each queue item to its node for the timeline
    const nodeQueueMap = new Map();
    queueItems.forEach((q) => {
      if (!nodeQueueMap.has(q.currentNodeId) || q.status !== 'completed') {
        nodeQueueMap.set(q.currentNodeId, q);
      }
    });

    const completedCount = completedNodeIds.size;
    const totalCount = actionNodes.length || 1;

    enrollments.push({
      userCampaign,
      template,
      nodes: orderedNodes,
      actionNodes,
      queueItems,
      nodeQueueMap,
      currentStep,
      completedNodeIds,
      failedNodeIds,
      progress: {
        completed: completedCount,
        total: totalCount,
        percentage: Math.round((completedCount / totalCount) * 100),
      },
      status: deriveEnrollmentStatus(queueItems),
      enrolledAt: queueItems[0]?.createdAt || null,
    });
  }

  return enrollments;
}

export function useContactCampaigns(contactId) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  const load = useCallback(async () => {
    if (!contactId) {
      setEnrollments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { queueItems, nodes } = await fetchContactCampaignEnrollments(contactId);
      setEnrollments(buildEnrollments(queueItems, nodes));
    } catch (err) {
      console.error('[useContactCampaigns] load error:', err);
      setError(err.message || 'Failed to load campaign enrollments');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!contactId) return;

    channelRef.current = subscribeToQueueByContact(contactId, () => {
      load();
    });

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [contactId, load]);

  const enrollContact = useCallback(
    async (userCampaignId) => {
      if (!contactId) throw new Error('No contactId');
      await enrollContactInCampaign(userCampaignId, contactId, {
        uid: currentUser?.uid,
        isAdmin: !!isCRMAdmin,
      });
      await load();
    },
    [contactId, load, currentUser?.uid, isCRMAdmin]
  );

  const unenrollContact = useCallback(
    async (userCampaignId) => {
      if (!contactId) throw new Error('No contactId');
      await unenrollContactFromCampaign(userCampaignId, contactId);
      await load();
    },
    [contactId, load]
  );

  const stats = useMemo(() => {
    const active = enrollments.filter((e) => e.status === 'active').length;
    const completed = enrollments.filter((e) => e.status === 'completed').length;
    const failed = enrollments.filter((e) => e.status === 'failed').length;
    return { total: enrollments.length, active, completed, failed };
  }, [enrollments]);

  return { enrollments, stats, loading, error, refetch: load, enrollContact, unenrollContact };
}
